import React, { useState, useEffect, useRef } from 'react';
import { Camera, Grid, List, Search, Info, Eye, TestTube, Clock, Edit, Users, Trash2, MoreVertical, X, Play, Pause, Volume2, VolumeX, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import useSnapshotWithFallback from '../hooks/useSnapshotWithFallback';
import Hls from 'hls.js';
import api from '../services/api';
import LoadingShimmer from './LoadingShimmer';
import PtzControls from './PtzControls';
import useStreamManager from '../hooks/useStreamManager';

// Thumbnail component for camera cards
const CameraThumbnail = ({ camera, onStreamStatus, refreshKey = 0 }) => {
  const { src, loading, failed } = useSnapshotWithFallback(camera._id, refreshKey, {
    gifTimeout: 15000, // Increased timeout for higher quality
    jpegTimeout: 8000,
    gifDuration: 3, // Increased duration for better preview
    gifFps: 12 // Increased FPS for smoother animation
  });

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-400 mx-auto mb-2"></div>
          <p className="text-xs text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={`${camera.name} snapshot`}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ imageRendering: 'auto' }}
      />
    );
  }

  return (
    <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
      <div className="text-center">
        <Camera className="h-10 w-10 text-gray-400 mx-auto mb-2" />
        <p className="text-xs text-gray-500">No Snapshot</p>
      </div>
    </div>
  );
};

const CameraGrid = ({
  cameras,
  viewMode = 'grid',
  onViewModeChange,
  searchTerm = '',
  onSearchChange,
  onStreamStatus,
  // Action handlers - Same as Cameras.jsx
  onEditCamera,
  onDeleteCamera,
  onToggleStatus,
  onAssignCamera,
  // Matrix layout props
  matrixLayout = 'auto',
  onMatrixLayoutChange,
  // Permission props
  hasPlaybackPermission = false,
  // Thumbnail refresh keys
  thumbnailKeys = {},
  // Location filtering props
  selectedLocation = 'ALL',
  locationOptions = ['ALL'],
  onLocationChange,
  // Action buttons props
  actionButtons = null
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);
  const [fullscreenCamera, setFullscreenCamera] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [sharingStats, setSharingStats] = useState({ totalViewers: 0, activeStreams: 0, efficiency: 0 });
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  
  // Stream manager for sharing integration
  const streamManager = useStreamManager();

  const toggleDropdown = (cameraId) => {
    setOpenDropdown(openDropdown === cameraId ? null : cameraId);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Update sharing stats periodically
  useEffect(() => {
    const updateStats = () => {
      const efficiency = streamManager.getSharingEfficiency();
      setSharingStats(efficiency);
    };
    
    updateStats();
    const interval = setInterval(updateStats, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, [streamManager]);

  const openFullscreenStream = async (camera) => {
    setFullscreenCamera(camera);
    setIsLoading(true);
    setError(null);
    setStreamUrl(null);

    try {
      console.log('Starting stream for camera:', camera._id, camera.name);
      const res = await api.get(`/cameras/${camera._id}/stream`);
      console.log('Stream response for camera:', camera.name, res.data);
      if (res.data.data.streamUrl) {
        setStreamUrl(res.data.data.streamUrl);
        console.log('Stream URL set successfully for camera:', camera.name);
      } else {
        throw new Error('No stream URL received');
      }
    } catch (error) {
      console.error('Stream start error for camera:', camera.name, error);
      setError('Connecting to camera...');
    } finally {
      setIsLoading(false);
    }
  };

  const closeFullscreenStream = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setFullscreenCamera(null);
    setStreamUrl(null);
    setError(null);
    setIsLoading(false);
    setIsPlaying(false);
    setIsMuted(false);
  };

  const getAbsoluteStreamUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const baseUrl = import.meta.env.VITE_API_STREAM_URL || import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3667';
    return baseUrl + url;
  };

  const initializeVideoPlayer = () => {
    if (!videoRef.current || !streamUrl) return;
    const absUrl = getAbsoluteStreamUrl(streamUrl);
    console.log('Initializing video player with URL:', absUrl);

    if (hlsRef.current) {
      hlsRef.current.destroy();
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.25,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        fragLoadingMaxRetry: 3,
        manifestLoadingMaxRetry: 3,
        levelLoadingMaxRetry: 3
      });
      hlsRef.current = hls;
      hls.loadSource(absUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('Stream manifest loaded successfully for camera:', fullscreenCamera.name);
        videoRef.current.play().catch((error) => {
          console.log('Autoplay failed, but video will still load:', error);
        });
      });
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS Error for camera:', fullscreenCamera.name, data);
        if (data.type === 'networkError' && data.details === 'manifestLoadError') {
          setError('Camera is starting up...');
        } else if (data.type === 'mediaError' && (data.details === 'bufferStalledError' || data.details === 'bufferSeekOverHole')) {
          return;
        } else if (data.fatal) {
          setError('Optimizing connection...');
        }
      });
      videoRef.current.onerror = (error) => {
        console.error('Video error occurred for camera:', fullscreenCamera.name, error);
        setError('Adjusting video quality...');
      };
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = absUrl;
      videoRef.current.play().catch((error) => {
        console.log('Autoplay failed for Safari, but video will still load:', error);
      });
    } else {
      setError('Updating video player...');
    }
  };

  useEffect(() => {
    if (streamUrl && videoRef.current) {
      initializeVideoPlayer();
    }
  }, [streamUrl]);

  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
      if (layoutDropdownOpen && !event.target.closest('.layout-dropdown')) {
        setLayoutDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown, layoutDropdownOpen]);

  // [MODIFIED] Calculate grid columns and sizing based on matrix layout
  const getGridClasses = () => {
    const cardAspectRatio = 'aspect-video'; // Enforce a 16:9 widescreen aspect ratio.
    let gridClass;
    let gapClass = 'gap-4'; // Default gap.

    if (matrixLayout === 'auto') {
      // Responsive columns for 'auto' mode.
      gridClass = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';
      gapClass = 'gap-4 lg:gap-5'; // Slightly larger gap for bigger screens.
    } else {
      // Fixed columns based on user selection (e.g., '3x3' -> 3 columns).
      const [cols] = matrixLayout.split('x').map(Number);
      gridClass = `grid-cols-${cols}`;
      
      // Reduce gap for denser layouts to save space.
      if (cols >= 4) {
        gapClass = 'gap-2 md:gap-3';
      }
    }

    return {
      grid: gridClass,
      card: cardAspectRatio,
      gap: gapClass
    };
  };



  // Enhanced search function with multiple criteria and fuzzy matching
  const searchCameras = (cameras, searchTerm) => {
    let filtered = cameras;

    // Apply text search
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

      filtered = filtered.filter(camera => {
        // Create a comprehensive search string from all camera properties
        const searchableText = [
          camera.name || '',
          camera.description || '',
          camera.location?.exactLocation || '',
          camera.location?.city || '',
          camera.location?.state || '',
          camera.location?.country || '',
          camera.ipAddress || '',
          camera.port || '',
          camera.username || '',
          camera.isOnline ? 'online' : 'offline',
          camera.isActive ? 'active' : 'inactive',
          camera.trackNumber ? `track ${camera.trackNumber}` : '',
          camera.assignedUsers?.map(user => user.name || user.username || '').join(' ') || '',
          camera.assignedUsers?.map(user => user.email || '').join(' ') || '',
          camera.createdAt ? new Date(camera.createdAt).toLocaleDateString() : '',
          camera.updatedAt ? new Date(camera.updatedAt).toLocaleDateString() : ''
        ].join(' ').toLowerCase();

        // Check if all search words are found in the searchable text
        return searchWords.every(word => searchableText.includes(word));
      });
    }

    // Apply location filtering
    if (selectedLocation !== 'ALL') {
      const locLower = selectedLocation.toLowerCase();
      filtered = filtered.filter(camera => {
        return (
          camera.location?.city?.toLowerCase().includes(locLower) ||
          camera.location?.state?.toLowerCase().includes(locLower) ||
          camera.location?.exactLocation?.toLowerCase().includes(locLower) ||
          camera.name?.toLowerCase().includes(locLower)
        );
      });
    }

    return filtered;
  };

  // Apply search and location filtering
  let filteredCameras = searchCameras(cameras, searchTerm);
  
  // Check for and fix duplicate camera IDs
  const seenIds = new Set();
  const duplicateIds = new Set();
  
  cameras.forEach(camera => {
    if (seenIds.has(camera._id)) {
      duplicateIds.add(camera._id);
    }
    seenIds.add(camera._id);
  });
  
  if (duplicateIds.size > 0) {
    // Remove duplicates by keeping only the first occurrence of each ID
    filteredCameras = filteredCameras.filter((camera, index, arr) => 
      arr.findIndex(c => c._id === camera._id) === index
    );
  }

  // Function to highlight search terms in text
  const highlightSearchTerms = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    let highlightedText = text;

    searchWords.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    });

    return highlightedText;
  };

  return (
    <div className="">
      {/* Header Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
        {/* Title */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Camera Overview</h2>
          <div className="flex items-center space-x-4">
            {/* Stream Sharing Stats */}
            {sharingStats.totalViewers > 0 && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Share2 className="h-4 w-4 text-blue-600" />
                <span>{sharingStats.totalViewers} viewers</span>
                <span className="text-gray-400">•</span>
                <span>{sharingStats.efficiency.toFixed(1)}x efficiency</span>
              </div>
            )}
            <div className="text-sm text-gray-500">
              {filteredCameras.length} of {cameras.length} cameras
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end mb-4">
          {/* Search Bar */}
          <div className="lg:col-span-5">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Search Cameras
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, location, IP, status, users, track #..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-9 pr-8 py-2 border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 rounded-md text-sm bg-gray-50 focus:bg-white"
              />
              {searchTerm && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Location Filter */}
          {locationOptions.length > 1 && onLocationChange && (
            <div className="lg:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Location
              </label>
              <select
                value={selectedLocation || 'ALL'}
                onChange={(e) => onLocationChange(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-md px-2.5 py-2 bg-gray-50 focus:bg-white text-gray-700 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                <option value="ALL">All Locations</option>
                {locationOptions.filter(loc => loc !== 'ALL').map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              View
            </label>
            <div className="flex bg-gray-100 rounded-md p-0.5">
              <button
                onClick={() => onViewModeChange('grid')}
                className={`flex-1 flex items-center justify-center px-2 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
                title="Grid View"
              >
                <Grid className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onViewModeChange('list')}
                className={`flex-1 flex items-center justify-center px-2 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
                title="List View"
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Layout Control */}
          {viewMode === 'grid' && onMatrixLayoutChange && (
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Layout
              </label>
              <div className="relative layout-dropdown">
                <button
                  onClick={() => setLayoutDropdownOpen(!layoutDropdownOpen)}
                  className="w-full flex items-center justify-between px-2.5 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:bg-white hover:bg-white text-gray-700 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <span className="capitalize">{matrixLayout}</span>
                  <Grid className="h-3.5 w-3.5 text-gray-400" />
                </button>
                {layoutDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      {['auto', '1x1', '2x2', '3x3', '4x4', '5x5'].map((layout) => (
                        <button
                          key={layout}
                          onClick={() => {
                            onMatrixLayoutChange(layout);
                            setLayoutDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors ${
                            matrixLayout === layout
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-gray-700'
                          }`}
                        >
                          <span className="capitalize">{layout}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons Section */}
        {actionButtons && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-3">
              {actionButtons}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Search Results Info */}
      {searchTerm && (
        <div className="flex items-center justify-between text-sm text-gray-600 bg-blue-50 border border-blue-200 px-3 py-2 rounded-md mb-3">
          <div className="flex items-center space-x-3">
            <Search className="h-4 w-4 text-blue-500" />
            <span>
              Found <span className="font-semibold text-blue-700">{filteredCameras.length}</span> of <span className="font-semibold">{cameras.length}</span> cameras
            </span>
            {filteredCameras.length > 0 && (
              <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded">
                "{searchTerm}"
              </span>
            )}
          </div>
          {filteredCameras.length === 0 && (
            <button
              onClick={() => onSearchChange('')}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Camera Display */}
      {filteredCameras.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 shadow-sm rounded-lg">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm ? "No cameras found" : "No cameras available"}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? `No cameras match "${searchTerm}". Try different search terms or clear the search.`
              : "No cameras are available at this time."}
          </p>
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
              <span>Clear Search</span>
            </button>
          )}
        </div>
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? `grid ${getGridClasses().grid} ${getGridClasses().gap}`
              : 'space-y-3'
          }
          data-matrix-layout={matrixLayout}
        >
          {filteredCameras.map((camera) => (
            <div
              key={camera._id}
              className={`bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-lg transition-all ${
                viewMode === 'list' ? 'p-4' : 'p-0'
              }`}
            >
              {viewMode === 'grid' ? (
                // Grid View with thumbnail and live stream toggle
                <>
                  <div className={`relative ${getGridClasses().card} bg-gray-100 rounded-t-xl overflow-hidden`}>
                        <CameraThumbnail
                          camera={camera}
                          onStreamStatus={onStreamStatus}
                          refreshKey={thumbnailKeys[camera._id] || 0}
                        />

                    {/* Status indicator */}
                    <div className="absolute top-2 left-2">
                      <div className={`rounded-full ${camera.isOnline ? 'bg-green-500' : 'bg-red-500'} ${
                        matrixLayout === '1x1' ? 'w-4 h-4' :
                        matrixLayout === '2x2' ? 'w-3 h-3' :
                        matrixLayout === '3x3' ? 'w-3 h-3' :
                        matrixLayout === '4x4' ? 'w-2 h-2' : 'w-2 h-2'
                      }`}
                           title={camera.isOnline ? 'Online' : 'Offline'}></div>
                    </div>

                    {/* Fullscreen Stream Button */}
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openFullscreenStream(camera);
                        }}
                        className={`rounded-lg transition-colors bg-black bg-opacity-50 text-white hover:bg-opacity-70 ${
                          matrixLayout === '1x1' ? 'p-3' :
                          matrixLayout === '2x2' ? 'p-2' :
                          matrixLayout === '3x3' ? 'p-2' :
                          matrixLayout === '4x4' ? 'p-1' : 'p-1'
                        }`}
                        title="Open Fullscreen Stream"
                      >
                        <Eye className={`${
                          matrixLayout === '1x1' ? 'h-5 w-5' :
                          matrixLayout === '2x2' ? 'h-4 w-4' :
                          matrixLayout === '3x3' ? 'h-4 w-4' :
                          matrixLayout === '4x4' ? 'h-3 w-3' : 'h-3 w-3'
                        }`} />
                      </button>
                    </div>

                    {/* Info Icon with Tooltip */}
                    <div className="absolute bottom-2 left-2">
                      <div className="relative group">

                        {/* Tooltip */}
                        <div className={`absolute left-0 bottom-8 bg-black bg-opacity-90 text-white rounded-lg p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                          matrixLayout === '1x1' ? 'w-72 text-sm' :
                          matrixLayout === '2x2' ? 'w-64 text-xs' :
                          matrixLayout === '3x3' ? 'w-56 text-xs' :
                          matrixLayout === '4x4' ? 'w-48 text-xs' : 'w-48 text-xs'
                        }`}>
                          <div className="space-y-1">
                            <div><strong>IP Address:</strong> {camera.ipAddress}:{camera.port}</div>
                            <div><strong>Type:</strong> {camera.type?.toUpperCase()}</div>
                            {camera.manufacturer && <div><strong>Manufacturer:</strong> {camera.manufacturer}</div>}
                            {camera.model && <div><strong>Model:</strong> {camera.model}</div>}
                            {camera.location?.exactLocation && <div><strong>Location:</strong> {camera.location.exactLocation}</div>}
                            {camera.description && <div><strong>Description:</strong> {camera.description}</div>}
                          </div>
                          <div className="absolute bottom-0 left-2 transform translate-y-1/2 w-2 h-2 bg-black bg-opacity-90 rotate-45"></div>
                        </div>
                      </div>
                    </div>

                    {/* Three Dots Menu - Moved to bottom-right */}
                    {(onEditCamera || onDeleteCamera || onToggleStatus || onAssignCamera) && (
                      <div className="absolute bottom-2 right-2">
                        <div className="relative dropdown-container">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(camera._id);
                            }}
                            className={`bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-lg transition-colors ${
                              matrixLayout === '1x1' ? 'p-3' :
                              matrixLayout === '2x2' ? 'p-2' :
                              matrixLayout === '3x3' ? 'p-2' :
                              matrixLayout === '4x4' ? 'p-1' : 'p-1'
                            }`}
                            title="More options"
                          >
                            <MoreVertical className={`${
                              matrixLayout === '1x1' ? 'h-5 w-5' :
                              matrixLayout === '2x2' ? 'h-4 w-4' :
                              matrixLayout === '3x3' ? 'h-4 w-4' :
                              matrixLayout === '4x4' ? 'h-3 w-3' : 'h-3 w-3'
                            }`} />
                          </button>

                          {/* Dropdown Menu */}
                          {openDropdown === camera._id && (
                            <div className={`absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[160px] ${
                              matrixLayout === '1x1' ? 'w-56' :
                              matrixLayout === '2x2' ? 'w-48' :
                              matrixLayout === '3x3' ? 'w-44' :
                              matrixLayout === '4x4' ? 'w-40' : 'w-40'
                            }`}>
                              <div className="py-1">
                                {/* Playback */}
                                {hasPlaybackPermission && (
                                  <Link
                                    to={`/camera-playback?cameraId=${camera._id}`}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={closeDropdown}
                                  >
                                    <Clock className="h-4 w-4 mr-3 text-green-600 flex-shrink-0" />
                                    <span className="truncate">Open Playback</span>
                                  </Link>
                                )}


                                {/* Edit Camera */}
                                {onEditCamera && (
                                  <button
                                    onClick={() => {
                                      onEditCamera(camera);
                                      closeDropdown();
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    <Edit className="h-4 w-4 mr-3 text-cyan-600 flex-shrink-0" />
                                    <span className="truncate">Edit Camera</span>
                                  </button>
                                )}

                                {/* Assign Camera */}
                                {onAssignCamera && (
                                  <button
                                    onClick={() => {
                                      onAssignCamera(camera);
                                      closeDropdown();
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    <Users className="h-4 w-4 mr-3 text-purple-600 flex-shrink-0" />
                                    <span className="truncate">Assign Camera</span>
                                  </button>
                                )}


                                {/* Toggle Status */}


                                {/* Delete Camera */}
                                {onDeleteCamera && (
                                  <button
                                    onClick={() => {
                                      onDeleteCamera(camera._id);
                                      closeDropdown();
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4 mr-3 flex-shrink-0" />
                                    <span className="truncate">Delete Camera</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`p-2 ${matrixLayout === '1x1' ? 'p-3' : matrixLayout === '2x2' ? 'p-2' : 'p-1.5'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`font-medium text-gray-900 truncate flex-1 mr-2 ${
                        matrixLayout === '1x1' ? 'text-base' :
                        matrixLayout === '2x2' ? 'text-sm' :
                        matrixLayout === '3x3' ? 'text-sm' :
                        matrixLayout === '4x4' ? 'text-xs' : 'text-xs'
                      }`} title={camera.name}>
                        {camera.name}
                      </h3>
                    </div>

                    {camera.location?.exactLocation && (
                      <p className={`text-gray-600 truncate ${
                        matrixLayout === '1x1' ? 'text-sm' :
                        matrixLayout === '2x2' ? 'text-xs' :
                        matrixLayout === '3x3' ? 'text-xs' :
                        'text-xs'
                      }`} title={camera.location.exactLocation}>
                        📍 {camera.location.exactLocation}
                      </p>
                    )}




                  </div>
                </>
              ) : (
                // List View with live stream
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        camera.isOnline ? "bg-green-500" : "bg-red-500"
                      }`}
                    ></div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {camera.name}
                      </h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          Type: {camera.type?.toUpperCase()} • IP: {camera.ipAddress}:{camera.port}
                        </p>
                        {camera.location?.exactLocation && (
                          <p>📍 {camera.location.exactLocation}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => openFullscreenStream(camera)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Live Stream</span>
                    </button>

                    {/* Three Dots Menu for List View */}
                    {(onEditCamera || onDeleteCamera || onToggleStatus || onAssignCamera) && (
                      <div className="relative dropdown-container">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(camera._id);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="More options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>

                        {/* Dropdown Menu */}
                        {openDropdown === camera._id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[160px]">
                            <div className="py-1">
                              {/* Playback */}
                              {hasPlaybackPermission && (
                                <Link
                                  to={`/camera-playback?cameraId=${camera._id}`}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  onClick={closeDropdown}
                                >
                                  <Clock className="h-4 w-4 mr-3 text-green-600 flex-shrink-0" />
                                  <span className="truncate">Open Playback</span>
                                </Link>
                              )}


                              {/* Edit Camera */}
                              {onEditCamera && (
                                <button
                                  onClick={() => {
                                    onEditCamera(camera);
                                    closeDropdown();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <Edit className="h-4 w-4 mr-3 text-cyan-600 flex-shrink-0" />
                                  <span className="truncate">Edit Camera</span>
                                </button>
                              )}

                              {/* Assign Camera */}
                              {onAssignCamera && (
                                <button
                                  onClick={() => {
                                    onAssignCamera(camera);
                                    closeDropdown();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <Users className="h-4 w-4 mr-3 text-purple-600 flex-shrink-0" />
                                  <span className="truncate">Assign Camera</span>
                                </button>
                              )}



                              {/* Delete Camera */}
                              {onDeleteCamera && (
                                <button
                                  onClick={() => {
                                    onDeleteCamera(camera._id);
                                    closeDropdown();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4 mr-3 flex-shrink-0" />
                                  <span className="truncate">Delete Camera</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}


            </div>
          ))}
        </div>
      )}

      {/* Fullscreen Video Player */}
      {fullscreenCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="bg-black rounded-lg overflow-hidden w-full max-w-6xl mx-4 max-h-[100vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gray-900 text-white">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${fullscreenCamera.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <h2 className="text-lg font-semibold">{fullscreenCamera.name}</h2>
                <span className="text-sm text-gray-400">
                  {fullscreenCamera.ipAddress}:{fullscreenCamera.port}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-2 hover:bg-gray-800 rounded"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
                <button
                  onClick={closeFullscreenStream}
                  className="p-2 hover:bg-gray-800 rounded"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Video Container */}
            <div className="relative bg-black">
              <PtzControls camera={fullscreenCamera} />
              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                {(isLoading || error) ? (
                  <div className="relative w-full h-full">
                    <LoadingShimmer />
                    {error && (
                      <div className="absolute bottom-4 right-4">
                        <button
                          onClick={() => openFullscreenStream(fullscreenCamera)}
                          className="bg-blue-600 bg-opacity-90 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm transition-colors backdrop-blur-sm"
                        >
                          Refresh
                        </button>
                      </div>
                    )}
                  </div>
                ) : streamUrl ? (
                  <video
                    ref={videoRef}
                    className="w-full h-full bg-black rounded"
                    controls
                    autoPlay
                    muted={isMuted}
                    style={{ display: 'block' }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onError={() => setError('Adjusting video quality...')}
                  />
                ) : (
                  <div className="text-center text-white">
                    <div className="text-6xl mb-4">📹</div>
                    <h3 className="text-xl font-semibold mb-2">Camera Stream</h3>
                    <p className="text-gray-400 mb-4">
                      {fullscreenCamera.type?.toUpperCase()} Stream from {fullscreenCamera.name}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Camera Info */}
            <div className="p-4 bg-gray-900 text-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Type:</span>
                  <span className="ml-2">{fullscreenCamera.type?.toUpperCase()}</span>
                </div>
                <div>
                  <span className="text-gray-400">Location:</span>
                  <span className="ml-2">{fullscreenCamera.location?.exactLocation || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Status:</span>
                  <span className={`ml-2 ${fullscreenCamera.isOnline ? 'text-green-400' : 'text-red-400'}`}>
                    {fullscreenCamera.isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Manufacturer:</span>
                  <span className="ml-2">{fullscreenCamera.manufacturer || 'Unknown'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraGrid;